import {
    ActionStatus,
    ActionStatusLookup,
    GamePlayerStatus,
    GamePlayerStatusLookup,
    TurnStatus,
    TurnStatusLookup
} from '../../Constants';
import artificialActionChooser from './ActionChooser/ActionChooser';
import artificialAdvertisementAction from './Actions/AIAdvertisement';
import artificialAntiJuice from './Actions/AIAntiJuice';
import artificialChooseFaction from './Actions/AIChooseFaction';
import artificialDraftAction from './Actions/AIDraft';
import artificialGhostAction from './Actions/AIGhostAction';
import artificialInitialLodging from './Actions/AIInitialLodging';
import artificialLodgingAction from './Actions/AILodging';
import artificialOpeningGhostPlacement from
    './Actions/AIOpeningGhostPlacement';
import artificialPlaceGhost from './Actions/AIPlaceGhost';
import artificialRandomAction from './Actions/AIRandomAction';
import artificialReceiveSpecial from './Actions/AIReceiveSpecialGuest';
import artificialRenovationAction from './Actions/AIRenovation';
import artificialResearchAction from './Actions/AIResearch';
import artificialScorePhase from './Actions/AIScorePhase';
import artificialStaffAction from './Actions/AIStaffAction';
import artificialSelectAction from './Actions/AISelectAction';
import artificialWaiting, {
    artificialActionComplete,
    artificialExecuteAction,
    artificialLocked
} from './Actions/Waiting';
import AIMessagePackage from './AIMessagePackage';
import SpeedController from './SpeedController';
import { REJECT_MESSAGE_EVENT } from '../Insights/InsightsWindow';


// CC_FEAI_REJECT_RETRY: a systematic FE<->engine mismatch must not
// ping-pong forever. After this many consecutive rejected retries with
// no accepted advance in between, the seat belays to manual controls
// instead of re-arming.
const MAX_CONSECUTIVE_REJECT_RETRIES = 3;


export const STATUS_ROUTES = {};
STATUS_ROUTES[GamePlayerStatus.PICK_PLAYER_ONE_ID] = null;
STATUS_ROUTES[GamePlayerStatus.PICK_FACTION_ID] = null;
STATUS_ROUTES[GamePlayerStatus.PICK_STATION_ID] = null;
STATUS_ROUTES[GamePlayerStatus.PICK_DRAFT_ID] = artificialDraftAction;
STATUS_ROUTES[GamePlayerStatus.GHOSTED_ID] = artificialPlaceGhost;
// 2026-05-16: GHOST_STARTER_ID is a transient backend state during
// Ghost Wave setup. Seats can get stuck there with attacking_ghosts
// > 0; treating it the same as GHOSTED_ID lets the AI place a ghost
// and unstick the chain. Backend's click_router now accepts clicks
// for this status too (routes them to message_while_ghosted).
STATUS_ROUTES[GamePlayerStatus.GHOST_STARTER_ID] = artificialPlaceGhost;
STATUS_ROUTES[GamePlayerStatus.DEFENDING_ID] = artificialAntiJuice;
STATUS_ROUTES[GamePlayerStatus.SPECIAL_ID] = artificialReceiveSpecial;  // receive special guest.
STATUS_ROUTES[GamePlayerStatus.SCORING_ID] = artificialScorePhase;
STATUS_ROUTES[GamePlayerStatus.ADVERTISING_ID] = artificialAdvertisementAction;
STATUS_ROUTES[GamePlayerStatus.RESEARCHING_ID] = artificialResearchAction;
STATUS_ROUTES[GamePlayerStatus.LODGING_ID] = artificialLodgingAction;
STATUS_ROUTES[GamePlayerStatus.ROBOTING_ID] = artificialStaffAction;
STATUS_ROUTES[GamePlayerStatus.RENOVATING_ID] = artificialRenovationAction;
STATUS_ROUTES[GamePlayerStatus.GHOSTING_ID] = artificialGhostAction;
STATUS_ROUTES[GamePlayerStatus.WAITING_ID] = artificialWaiting;
STATUS_ROUTES[GamePlayerStatus.RANDOM_ID] = artificialRandomAction;
STATUS_ROUTES[GamePlayerStatus.GAME_SETUP_ID] = artificialInitialLodging;
// Manual 3.1 opening ghost placement. The seat lands here after game
// launch (before Initial Lodging) when config['ghosts'] is on.
STATUS_ROUTES[GamePlayerStatus.OPENING_GHOST_PLACEMENT_ID] =
    artificialOpeningGhostPlacement;
// Manual §3 in-game faction ceremony. Clockwise from Player One,
// each seat commits its faction. Auto-confirm path mirrors backend
// Fossa _choose_faction handler.
STATUS_ROUTES[GamePlayerStatus.CHOOSE_FACTION_ID] = artificialChooseFaction;
// 2026-05-15: Once the game ends the seat lands in WIN/TIED/GAME_OVER.
// The AI has no decisions left — route to the same no-op as WAITING so
// the suggestion panel says something benign instead of "I am not sure
// what to do in this situation."
STATUS_ROUTES[GamePlayerStatus.WIN_ID] = artificialWaiting;
STATUS_ROUTES[GamePlayerStatus.GAME_OVER_ID] = artificialWaiting;
STATUS_ROUTES[GamePlayerStatus.TIED_ID] = artificialWaiting;


const TURN_STATUS_ROUTES = {};
TURN_STATUS_ROUTES[TurnStatus.ACTION_COMPLETE_ID] = artificialActionComplete;
TURN_STATUS_ROUTES[TurnStatus.CHOOSE_ACTIONS_ID] = artificialActionChooser;
TURN_STATUS_ROUTES[TurnStatus.SELECT_ACTION_ID] = artificialSelectAction;
TURN_STATUS_ROUTES[TurnStatus.EXECUTE_ACTION_ID] = artificialExecuteAction;
TURN_STATUS_ROUTES[TurnStatus.LOCKED_ID] = artificialLocked;


// CC_RESTORE_OWNER_ONLY_AI_DRIVER (2026-06-04): the
// TERMINAL_DECISION_STATUSES set + its empowered-gate bypass that lived
// here (CC_AI_REFERRAL_STALL_HARDEN, 2026-06-02) are gone. The owner
// (group leader) is now the SOLE FE-AI driver for every status. The
// game-531 deadlock the bypass guarded against is handled structurally
// instead — action-chain enforcement rejects stale duplicate clicks at
// the protocol layer (engine/clicks/click_router._check_action_chain)
// and the reconnect path re-engages the owner's AI on return. See
// shouldAIUpdate below.


function aiRouter(gamePlayerController, aiPackage) {
    let statusId = gamePlayerController.gamePlayerData.status;
    if (statusId in STATUS_ROUTES) {
        const route = STATUS_ROUTES[statusId];
        if (route !== null) {
            return route(gamePlayerController, aiPackage);
        }
    }
    let turnStatus = gamePlayerController.gamePlayerData.turn_status;
    if (turnStatus in TURN_STATUS_ROUTES) {
        const route = TURN_STATUS_ROUTES[turnStatus];
        if (route !== null) {
            return route(gamePlayerController, aiPackage);
        }
    }
    // CC_AUTOPLAY_DEDUP_SILENT_DROP always-act closure: no route matched this
    // seat's status/turn_status, so no Actions/* handler emitted a click. On
    // the SUGGEST pass (execute=false) narrating is correct — it is
    // side-effect-free. But on the EXECUTE pass, a seat this client drives
    // parked at a decision state with ZERO clicks emitted is the AI-always-act
    // violation (HSH_STYLE §AI always-act, game 807: "a silent return is the
    // only illegal move"). Close the branch with the explicit decline
    // (clickNext -> 'Next'), exactly as every Actions/* route closes its own
    // always-act branch (see AIPlaceGhost). clickNext routes through the same
    // send seam, so a non-click status is still harmlessly dropped by
    // _statusAcceptsClicks and the engine validate-first owns the decline;
    // either way the seat gets a patch/reject back instead of stranding. The
    // routed no-op states (WAITING / WIN / GAME_OVER / TIED -> artificialWaiting)
    // never reach here — they match a route above — so this does not spam Next
    // at idle seats.
    if (aiPackage.execute) {
        gamePlayerController.gamePlayerApi.clickNext();
    }
    aiPackage.addToMessage('I am not sure what to do in this situation.');
}


export class ArtificialPlayer {
    constructor(gamePlayerController) {
        this.gamePlayerController = gamePlayerController;
        this.seat = gamePlayerController.seat;
        const gamePlayerData = this.gamePlayerController.gamePlayerData;
        this.memory = {
            name: 'Default AI',
            actions: [],
            currentPrimaryAction: null,
        };
        this.currentAIPackage = null;
        this.lastAIPackage = null;
        this.lastAIPackageExecution = null;
        this.speedController = new SpeedController(this);
        this.previousStatus = {
            gamePlayerStatus: gamePlayerData.status,
            turnStatus: gamePlayerData.turn_status,
            actionStatus: gamePlayerData.action_status,
            robotAssignments: gamePlayerData.robot_assignments,
            station: gamePlayerData.station,
            docks: gamePlayerData.docks,
            currentPlayerId: null,
        }
        this.modified = performance.now();

        this.autoExecute = null;

        // CC_FEAI_REJECT_RETRY: reject-armed one-shot retry state. A typed
        // engine Reject for a seat this client drives sets retryArmed; the
        // next GamePlayer patch for the seat consumes it and fires the AI
        // even though no hasStatusChanged-tracked field moved (the rearm
        // patch only rotates last_action_uuid). consecutiveRejectRetries
        // breaks a systematic reject<->retry loop.
        this.retryArmed = false;
        this.consecutiveRejectRetries = 0;

        // CC_FEAI_REARM_ON_RECONNECT: deferred-drive one-shot for the
        // reconnect race. Leadership (Members) and game state (Resume) arrive
        // as independent, unordered async pushes; a Feai seat's activating
        // turn can land while isGroupLeader() is still false, so shouldAIUpdate
        // drops the drive (owner-only gate) and — because that activating patch
        // is the last tracked movement the seat receives — the existing
        // groupUpdateEvent re-drive finds nothing left for hasStatusChanged() to
        // report and the bot freezes. This latch records the dropped drive and
        // updateEvent replays it exactly once when leadership establishes
        // (isGroupLeader() false -> true). Mirrors the retryArmed seam above:
        // latch a drive the diff gate can't re-present, replay it once. The
        // owner-only invariant holds — the latch only consumes while empowered,
        // so a client that is never the leader never drives.
        this.reconnectDriveArmed = false;

        // CC_FEAI_EMPOWERED_CURRENT_SEAT_STALL: empowered current-seat liveness
        // one-shot. The owner-only diff gate (shouldAIUpdate) drives a Feai
        // seat only when a hasStatusChanged-tracked field moves. But a
        // post-Execute backend patch can rotate last_action_uuid while moving
        // NO tracked field (e.g. Execute Research returns the seat to the same
        // status/turn owing its next action) — so hasStatusChanged() is false,
        // no Reject arms retryArmed, and (already the leader) no leadership
        // transition arms reconnectDriveArmed. The seat is current_player,
        // owes a move, and nothing re-drives it: the silent freeze in
        // production game G75 (2026-07-09), where a hard refresh did NOT
        // recover it and only Main-Menu -> Resume did — because Resume rebuilds
        // group membership and trips the reconnectDriveArmed latch, a path the
        // ALREADY-empowered branch never had. This latch gives the empowered
        // path the same "it is my turn, drive even without a tracked change"
        // liveness. shouldAIUpdate arms it; updateEvent replays the drive
        // exactly once. empoweredDriveHead de-dups by chain head so the several
        // window events fanning out from ONE broadcast (per-seat, group-update,
        // game) drive at most once — the head is a de-dup key, the decision
        // gate (_owesEmpoweredDrive) is what decides a drive is owed. It starts
        // null, matching the controller's lastActionUuid before any patch: the
        // fallback only fires once a genuine backend broadcast has rotated the
        // head to a non-null value (every real action-chain patch carries one),
        // so a seat that has never received a chain head is never
        // fallback-driven. Mirrors the retryArmed / reconnectDriveArmed seams:
        // latch a drive the diff gate cannot re-present, replay it once,
        // owner-only.
        this.empoweredDriveArmed = false;
        this.empoweredDriveHead = null;
        // CC_FEAI_EMPOWERED_GHOST_DRIVE_LOOP (G116, 2026-07-12): the
        // empoweredDriveHead de-dup above is necessary (fan-out within one
        // broadcast) but NOT sufficient. It keys ONLY on the chain head, which
        // rotates on EVERY backend broadcast — so a no-progress re-broadcast of
        // the SAME owed decision (new head, identical getStatus()) re-passes the
        // head check and re-arms, driving the same click again and again. The
        // original CC's claim that "the reject-retry belay (cap 3) bounds the
        // loop" is false on this path: a click that dispatches but does not
        // advance produces a stale parent_action_uuid, which
        // click_router._check_click_protocol logs as telemetry and PROCEEDS —
        // it is never a typed Reject, so consecutiveRejectRetries never counts
        // it and belay() never trips. Result: the owner's browser spins
        // "Execute Ghost" every broadcast (the G116 Ghost-Wave freeze; refresh
        // re-adopts the driver and re-enters the same spin, so "neither refresh
        // nor reload works"). The fix realizes the invariant the original CC
        // SPECIFIED but did not implement — "key the one-shot on the decision,
        // not the chain head": remember the decision-state signature we last
        // drove and never re-drive an unchanged decision. A genuine advance
        // moves a getStatus()-tracked field (docks on a landed ghost, status on
        // a turn edge), which changes the signature and re-opens the gate.
        this.empoweredDriveStateKey = null;

        window.addEventListener(
            this.gamePlayerController.eventName,
            this.updateEvent);
        window.addEventListener(
            'group-update-event',
            this.groupUpdateEvent);
        // 2026-05-14: Phase 0.8 Initial Lodging Round advances
        // game.current_player without touching the new active
        // seat's GamePlayer row, so its game-player-N-event never
        // fires. Listen for the game-level event too so the AI
        // for the seat that just became current_player gets a
        // chance to act on its turn.
        window.addEventListener(
            'game-event',
            this.gameUpdateEvent);
        // CC_FEAI_REJECT_RETRY: listen for typed engine Rejects so a
        // rejected Feai click can re-fire on the rearm patch. Added the
        // same way as the three listeners above; like them it is never
        // removed (this class has no teardown — flagged in the hand-back).
        window.addEventListener(
            REJECT_MESSAGE_EVENT,
            this.rejectEvent);
    }

    groupUpdateEvent = () => {
        if (!this.speedController.isEngaged()) {
            this.updateEvent();
        }
    }

    gameUpdateEvent = () => {
        // 2026-05-15: Narrowed from "fire on every game-event" to
        // "fire only during the Initial Lodging Round." The broad
        // listener was double-firing the AI during normal turns:
        // once during the recursive game-event dispatch (with stale
        // per-seat data) and again when the matching
        // game-player-event arrived. Each fire sent its own Click,
        // and the second one was the wrong shape for the new state
        // — hence the "Advertisement / place" arriving during
        // SELECT_ACTION and the "ActionGrid" arriving during
        // ADVERTISING errors. During normal turn-taking the
        // game-player-event drives the AI just fine; only the
        // ceremonies whose clockwise advance mutates ONLY
        // game.current_player (not any per-seat row) need this
        // trigger.
        // 2026-05-18: Manual §3 in-game faction ceremony has the
        // same shape as Initial Lodging — _advance_or_finish in
        // engine/round_choices/choose_faction.py saves game with a
        // new current_player but doesn't touch the next picker's
        // GamePlayer row, so its game-player-N-event never fires
        // (GamePlayerController.updateGamePlayerData short-circuits
        // on the JSON.stringify diff). Without this extra gate the
        // AI for seats 1..5 just sits in status 33 indefinitely.
        const status =
            this.gamePlayerController.gamePlayerData.status;
        if (status !== GamePlayerStatus.GAME_SETUP_ID
                && status !== GamePlayerStatus.CHOOSE_FACTION_ID) {
            return;
        }
        // 2026-07-10: drive REGARDLESS of engagement. In both ceremony
        // rounds the clockwise advance mutates ONLY game.current_player
        // (the newly-active seat's per-seat row is untouched — the reason
        // this game-event listener exists), so the game-event is the SOLE
        // signal that it is the local seat's turn. When the local seat has
        // auto engaged (its SpeedController IS engaged — unlike a Feai
        // opponent seat, which is never hand-engaged), an `isEngaged()`
        // guard here swallowed that lone signal: a non-Player-One seat sat
        // on its stale "Waiting for my turn" suggestion and never acted
        // until a manual belay + re-auto. updateEvent + waitToConcur/concur
        // own the countdown coordination, and hasStatusChanged() de-dups a
        // same-advance re-eval, so an unconditional drive here does not
        // double-fire. (groupUpdateEvent keeps its guard — a group/Members
        // broadcast is NOT a turn signal, and driving on every one would
        // reset the engaged countdown during normal multi-seat play.)
        this.updateEvent();
    }

    updateEvent = () => {
        if (this.autoExecute === null &&
            this.gamePlayerController.gamePlayerData.artificial_player > 2) this.autoExecute = true;
        // CC_RECRUIT_AI_PERSIST: a seat taken over by a human mid-game
        // (recruit / Request-a-Seat) broadcasts a per-seat GamePlayer patch
        // with artificial_player cleared. The sticky autoExecute that made
        // THIS client self-play the AI seat would otherwise persist (it is
        // set once and never reset above), so the owner's driver keeps
        // playing a seat that is now a human's. Relinquish to advisor-only
        // the moment artificial_player goes null: shouldAIUpdate already
        // takes the human (=== null) branch, but only clearing autoExecute
        // stops the concur() self-play on the seat's next status change.
        if (this.gamePlayerController.gamePlayerData.artificial_player === null
                && this.autoExecute) {
            this.autoExecute = false;
            this.retryArmed = false;
        }
        const advanced = this.shouldAIUpdate();
        if (advanced) {
            // Genuine state advance: the prior click was accepted-
            // equivalent, so the consecutive-reject streak is broken
            // (CC_FEAI_REJECT_RETRY).
            this.consecutiveRejectRetries = 0;
        }
        // A rejected Feai click rotates last_action_uuid via rearm() but
        // moves no hasStatusChanged-tracked field, so shouldAIUpdate() is
        // false on the rearm patch. The reject-armed one-shot fires the AI
        // anyway, on that patch, so the seat isn't stranded
        // (CC_FEAI_REJECT_RETRY). The owner-only gate was enforced when the
        // flag was armed in rejectEvent.
        const retry = this.retryArmed && !advanced;
        if (retry) {
            this.retryArmed = false;
        }
        // CC_FEAI_REARM_ON_RECONNECT: consume the deferred-drive latch armed by
        // shouldAIUpdate when this seat's turn arrived while un-empowered. It
        // fires ONLY once leadership is established (empowered) — the owner-only
        // invariant (CC_RESTORE_OWNER_ONLY_AI_DRIVER) is preserved: a client
        // that never becomes leader never consumes, so it never drives. Gated
        // on !advanced && !retry so the diff gate and the reject one-shot each
        // own their own patch and the three seams cannot stack into a double
        // drive. Correct for BOTH push orderings: if state arrived first the
        // latch is already armed and this empowered re-eval replays it; if
        // leadership arrived first shouldAIUpdate never took the un-empowered
        // branch, nothing armed, and the seat drives through the normal
        // hasStatusChanged path when its state lands.
        const empowered =
            this.gamePlayerController.game.groupHandler.isGroupLeader();
        const reconnectRetry =
            this.reconnectDriveArmed && empowered && !advanced && !retry;
        // CC_FEAI_EMPOWERED_CURRENT_SEAT_STALL: consume the empowered
        // current-seat fallback latch armed by shouldAIUpdate when an empowered
        // seat is current_player and owes a move but no tracked field moved
        // (the G75 no-progress freeze). Gated LAST so the diff gate, the reject
        // one-shot, and the reconnect one-shot each own their own patch — the
        // four seams cannot stack into a double drive. `empowered` is re-checked
        // here (belt-and-suspenders with the arm-time gate) so the owner-only
        // invariant holds even if leadership dropped between arm and eval.
        const empoweredCurrentDrive =
            this.empoweredDriveArmed && empowered
            && !advanced && !retry && !reconnectRetry;
        if (advanced || retry || reconnectRetry || empoweredCurrentDrive) {
            // Any genuine drive satisfies the deferred latch — clear it so a
            // later patch can't replay a stale turn (one-shot, like retryArmed).
            this.reconnectDriveArmed = false;
            this.empoweredDriveArmed = false;
            // One drive per backend broadcast: record the chain head just
            // driven so a same-broadcast re-eval (the per-seat event, then
            // group-update-event, then game-event all fan out from ONE patch)
            // cannot re-fire the empowered fallback. This also stops a normal
            // diff-gate drive from leaking into a fallback drive on the very
            // next no-change re-eval — the head matches, so it does not re-arm.
            this.empoweredDriveHead = this.gamePlayerController.lastActionUuid;
            // CC_FEAI_EMPOWERED_GHOST_DRIVE_LOOP: record the decision-state we
            // just drove. The empowered fallback will NOT re-arm while the seat
            // owes this same (unchanged) decision on a later head-rotating
            // re-broadcast — only a genuine advance (a moved getStatus() field)
            // changes this signature and re-opens the gate. Set on EVERY drive
            // (diff-gate, reject-retry, reconnect, or fallback) so all paths
            // share one "last driven decision" record and cannot double-drive
            // the same decision across broadcasts.
            this.empoweredDriveStateKey = JSON.stringify(this.getStatus());
            console.log('AI UPDATE EVENT', this.seat);
            this.previousStatus = this.getStatus();
            this.queryTheNextThing();
            if (this.autoExecute) {
                // CC_AUTO_DRIVER_SINGLE_EXECUTE: an autoExecute seat
                // self-plays. queryTheNextThing only built the suggestion, so
                // the single execution happens here, exactly once, through
                // the canonical concur path. stopCountdown() cancels any
                // pending engaged countdown so it cannot deliver a second
                // concur for this patch — the eager-execute + engaged-concur
                // stacking was the double fire. Feai seats are never
                // hand-engaged (the speed UI binds only to the seated
                // controller), so concur is driven directly here rather than
                // through the countdown, preserving immediate self-play.
                this.speedController.stopCountdown();
                this.concur();
            } else {
                // Human-advised seat: show the suggestion and let the
                // SpeedController concur — after the countdown when engaged,
                // or on the human's explicit Concur in manual mode.
                this.speedController.waitToConcur();
            }
            this.notifyOfUpdate();
        } else if (
                this.gamePlayerController.gamePlayerData.artificial_player > 2
                && empowered && this._owesEmpoweredDrive()
                && this.gamePlayerController.lastActionUuid
                    !== this._lastNoDriveDiagnosedHead) {
            // DIAGNOSTIC (CC_FEAI_ROBOTING_REDRIVE_STALL, G121 2026-07-12): an
            // owned Feai seat that is current_player and owes a REAL move
            // (_owesEmpoweredDrive) received a fresh broadcast but NONE of the
            // four drive seams fired — the silent re-drive stall that only a
            // reload clears. Log the seam + guard values so the next live
            // recurrence pins which latch was suppressed (leading suspect: the
            // empoweredDriveStateKey block, CC_FEAI_EMPOWERED_GHOST_DRIVE_LOOP)
            // instead of stalling with no evidence. Pure logging, no behavior
            // change. Gated on a rotated chain head so same-broadcast window-
            // event fanout (per-seat -> group -> game) logs at most once.
            const gp = this.gamePlayerController.gamePlayerData;
            this._lastNoDriveDiagnosedHead =
                this.gamePlayerController.lastActionUuid;
            console.warn(
                `s${this.seat} - FEAI NO-DRIVE, owes move but stalled: `
                + `advanced=${advanced} retry=${retry} `
                + `reconnectRetry=${reconnectRetry} `
                + `empoweredCurrentDrive=${empoweredCurrentDrive} | `
                + `retryArmed=${this.retryArmed} `
                + `empoweredDriveArmed=${this.empoweredDriveArmed} `
                + `head=${this.gamePlayerController.lastActionUuid} `
                + `empoweredDriveHead=${this.empoweredDriveHead} `
                + `stateKeyUnchanged=`
                + `${JSON.stringify(this.getStatus())
                    === this.empoweredDriveStateKey} | `
                + `status=${gp.status} turn_status=${gp.turn_status} `
                + `action_status=${gp.action_status}`);
        }
    }

    rejectEvent = (event) => {
        const detail = event && event.detail;
        const rejectedId = this._extractRejectGamePlayerId(detail);
        if (rejectedId === null) {
            return;
        }
        const gamePlayerData = this.gamePlayerController.gamePlayerData;
        // Each seat's ArtificialPlayer hears every Reject; only the one
        // whose seat the Reject targets reacts.
        if (Number(rejectedId) !== Number(gamePlayerData.id)) {
            return;
        }
        // CC_ENGAGED_ADVISOR_SINGLE_EXECUTE: a reject for this seat means the
        // last click did not take. Clear the send-layer duplicate latch (see
        // GamePlayerPackageSender.sendPackage) so a re-send of the same click
        // — the reject-retry, or a human's manual re-Concur — is not mistaken
        // for the duplicate the latch suppresses.
        this.gamePlayerController._lastClickFingerprint = null;
        this.gamePlayerController._lastClickLifecycle = null;
        // Only a seat THIS client is auto-driving re-fires on reject. Two
        // drive modes qualify: (a) a Feai seat this client owns — mirror the
        // shouldAIUpdate owner-only gate (isFeai && empowered); (b) the
        // own-seat auto this client self-plays — this.autoExecute, the
        // sticky flag updateEvent sets for a seat it drives via concur
        // (CC_AUTO_DRIVER_SINGLE_EXECUTE). Without (b) an own-seat-auto
        // reject gets the rearm patch but never re-fires: "auto" silently
        // pauses until the human notices and clicks (CC_AUTO_SEAT_REJECT_RETRY).
        // A hand-played human seat (artificial_player === null → autoExecute
        // never set) stays un-armed — its rejected click is the human's to
        // retry, and InsightsWindow renders the correction.
        const empowered =
            this.gamePlayerController.game.groupHandler.isGroupLeader();
        const isFeai = gamePlayerData.artificial_player
            && gamePlayerData.artificial_player > 2;
        if (!((isFeai && empowered) || this.autoExecute)) {
            return;
        }
        this.consecutiveRejectRetries += 1;
        if (this.consecutiveRejectRetries > MAX_CONSECUTIVE_REJECT_RETRIES) {
            // Loop-breaker: a systematic FE<->engine mismatch must not
            // ping-pong forever. Surface manual controls instead of
            // re-arming.
            console.warn(
                `s${this.seat} - reject retry cap reached `
                + `(${this.consecutiveRejectRetries}); belaying.`);
            this.belay();
            return;
        }
        this.retryArmed = true;
    };

    _extractRejectGamePlayerId = (detail) => {
        if (!detail) {
            return null;
        }
        // The Reject arrives as the full OutgoingMessage wrapper
        // ({type, game, datetime, package: <RejectWire>}), so the
        // RejectWire's game_player id lives at detail.package.game_player.
        // A flat RejectWire (the shape InsightsWindow + its tests assume)
        // carries it at detail.game_player. Read nested first, fall back to
        // flat. See the hand-back note on the InsightsWindow shape.
        const wire = (detail.package && typeof detail.package === 'object')
            ? detail.package : detail;
        const id = wire.game_player;
        return (id === undefined || id === null) ? null : id;
    };

    notifyOfUpdate = () => {
        window.dispatchEvent(new Event('artificial-player-update-event'));
    }

    getStatus = () => {
        const gamePlayerData = this.gamePlayerController.gamePlayerData;
        // current_player is a game-level FK to GamePlayer.id; we
        // track it here so seat transitions during the Initial
        // Lodging Round (and any other turn-by-turn flow that
        // doesn't mutate the per-seat row) still trip
        // hasStatusChanged and let the AI react.
        const game = this.gamePlayerController.game;
        const gameData = (game && game.gameData) || null;
        return {
            gamePlayerStatus: gamePlayerData.status,
            turnStatus: gamePlayerData.turn_status,
            actionStatus: gamePlayerData.action_status,
            robotAssignments: gamePlayerData.robot_assignments,
            station: gamePlayerData.station,
            docks: gamePlayerData.docks,
            lodging: gamePlayerData.lodging,
            currentPlayerId: gameData ? gameData.current_player : null,
        };
    }

    hasStatusChanged = () => {
        const newStatus = this.getStatus();
        let changed = false;
        for (let [key, value] of Object.entries(newStatus)) {
            if (JSON.stringify(this.previousStatus[key]) !== JSON.stringify(value)) {
                changed = true;
                let displayValue = value;
                let displayPrevious = this.previousStatus[key];
                if (key === 'actionStatus') {
                    displayValue = ActionStatusLookup[displayValue];
                    displayPrevious = ActionStatusLookup[displayPrevious];
                }
                if (key === 'turnStatus') {
                    displayValue = TurnStatusLookup[displayValue];
                    displayPrevious = TurnStatusLookup[displayPrevious];
                }
                if (key === 'gamePlayerStatus') {
                    displayValue = GamePlayerStatusLookup[displayValue];
                    displayPrevious = GamePlayerStatusLookup[displayPrevious];
                }
                console.log(`s${this.seat} - ${key} changed from ${displayPrevious} to ${displayValue}`)
            }
        }
        return changed;
    }

    shouldAIUpdate = () => {
        const empowered = this.gamePlayerController.game.groupHandler.isGroupLeader();
        const gamePlayerData = this.gamePlayerController.gamePlayerData;
        // Human seat (no AI persona): unchanged legacy behavior.
        if (gamePlayerData.artificial_player === null) {
            return this.hasStatusChanged();
        }
        // Frontend-AI seat (Feai, id > 2). Backend-AI seats (1, 2) fall
        // through to undefined — the backend drives them.
        // CC_RESTORE_OWNER_ONLY_AI_DRIVER (2026-06-04): the owner (group
        // leader) is the SOLE driver of the FE AI, for EVERY status —
        // terminal-decision ones included. The earlier
        // TERMINAL_DECISION_STATUSES bypass (CC_AI_REFERRAL_STALL_HARDEN,
        // 2026-06-02) let every connected client drive terminal-decision
        // clicks to dodge the game-531 deadlock; that overcorrected into
        // the duplicate-click pattern that drove the G45 chain. The
        // deadlock is now handled structurally, not by fanning clicks
        // out: the action-chain enforcement in
        // engine/clicks/click_router._check_action_chain rejects stale
        // duplicate clicks at the protocol layer (only the chain-head
        // driver advances), and the reconnect path (SocketHandler
        // ws-open-event → group rendezvous → fresh state push) re-engages
        // the owner's AI when the owner returns mid-decision.
        // CC_FEAI_REJECT_RETRY (2026-06-04): the structural claim above
        // covered DETECTION but not RECOVERY. When the engine rejects a
        // Feai click it rearms (rotates last_action_uuid, re-broadcasts)
        // but only re-drives BACKEND AIs; the FE AI never re-fires because
        // the rearm patch changes no hasStatusChanged-tracked field. Game
        // 46 (faction-ceremony deadlock) stalled on exactly this. Recovery
        // now lives in this class: a Reject for a seat this client drives
        // arms a one-shot retry (rejectEvent) that fires on the next
        // GamePlayer patch — the rearm patch, which carries the fresh chain
        // head. Without it a single reject strands the seat.
        if (gamePlayerData.artificial_player && gamePlayerData.artificial_player > 2) {
            if (empowered) {
                if (this.hasStatusChanged()) {
                    return true;
                }
                // CC_FEAI_EMPOWERED_CURRENT_SEAT_STALL: empowered, but no
                // tracked field moved. A post-Execute backend patch can rotate
                // last_action_uuid (so the seat event fires) while moving no
                // tracked field, leaving this seat current_player and owing its
                // next move with nothing to re-drive it — the G75 freeze. Arm
                // the empowered current-seat fallback IFF this seat genuinely
                // owes a move (_owesEmpoweredDrive: current_player AND a real AI
                // route, not the artificialWaiting no-op states) AND we have not
                // already driven THIS backend broadcast. The chain head is the
                // de-dup key only — one broadcast fans out to several window
                // events, each re-running updateEvent, and without the head
                // check the fallback would fire on each. Keying the guard on the
                // head ALONE is not enough (it rotates on every broadcast, so it
                // would drive on unrelated re-broadcasts); the decision gate is
                // the primary key. empoweredDriveHead starts null, so a seat that
                // has received no chain head yet (head still null) never arms —
                // only a broadcast that rotated the head to a non-null value can.
                // A stale re-eval (same head, e.g. a group-update-event fanning
                // out) is dropped here. hasStatusChanged() is read-only, so
                // calling it above has no effect on the gate.
                //
                // CC_FEAI_EMPOWERED_GHOST_DRIVE_LOOP (G116, 2026-07-12): the
                // head check alone let a no-progress re-broadcast (new head,
                // identical decision) re-arm forever — the belay does NOT bound
                // it because a stale-parent mismatch is telemetry, not a Reject.
                // Add the decision-state gate the original CC specified but did
                // not build: do not re-arm while the seat still owes the SAME
                // decision-state we already drove (empoweredDriveStateKey). A
                // genuine advance moves a getStatus()-tracked field, changing
                // the signature and re-opening the gate for the next decision.
                const head = this.gamePlayerController.lastActionUuid;
                const stateKey = JSON.stringify(this.getStatus());
                if (head !== this.empoweredDriveHead
                        && stateKey !== this.empoweredDriveStateKey
                        && this._owesEmpoweredDrive()) {
                    this.empoweredDriveArmed = true;
                }
                return false;
            }
            // CC_FEAI_REARM_ON_RECONNECT: un-empowered Feai. The owner-only gate
            // means this client may not drive the seat yet, so we still return
            // falsy (no drive here). But on a reconnect the leadership push can
            // lag the state push, and the drive we are dropping right now may be
            // the seat's LAST tracked movement — losing it strands the bot until
            // a manual refresh (which re-runs the identical losing race). Arm a
            // one-shot deferred drive when there is a real drivable situation:
            // either a tracked change the diff gate can still see
            // (hasStatusChanged), OR simply that it is this seat's turn
            // (_isCurrentPlayer) — the reconnect-race shape, where the resumed
            // state is already baked into previousStatus so hasStatusChanged is
            // false yet the seat genuinely owes a move. updateEvent replays it
            // once leadership establishes. hasStatusChanged() is read-only (it
            // logs the diff but does not advance previousStatus), so evaluating
            // it here has no side effect on the gate.
            if (this.hasStatusChanged() || this._isCurrentPlayer()) {
                this.reconnectDriveArmed = true;
            }
        }
    }

    _isCurrentPlayer = () => {
        const game = this.gamePlayerController.game;
        const gameData = (game && game.gameData) || null;
        const gamePlayerData = this.gamePlayerController.gamePlayerData;
        return !!gameData && gameData.current_player === gamePlayerData.id;
    }

    _owesEmpoweredDrive = () => {
        // CC_FEAI_EMPOWERED_CURRENT_SEAT_STALL: the empowered fallback fires
        // ONLY for a seat that genuinely owes a move — it must be this seat's
        // turn (current_player) AND route to a real AI action. The
        // artificialWaiting no-op states (WAITING / WIN / GAME_OVER / TIED all
        // map to artificialWaiting in STATUS_ROUTES) are resting seats: poking
        // them would spam the narrate-only handler. A non-current or resting
        // seat returns false and never arms the latch.
        if (!this._isCurrentPlayer()) {
            return false;
        }
        const statusId = this.gamePlayerController.gamePlayerData.status;
        return STATUS_ROUTES[statusId] !== artificialWaiting;
    }


    setMemory = (key, value) => {
        this.memory[key] = value;
    };

    _buildNewAIPackage = (execute = false) => {
        this.currentAIPackage = new AIMessagePackage(
            this.gamePlayerController, this, execute);
        this.currentAIPackage.name = this.memory.name;
    };

    queryTheNextThing = () => {
        // CC_AUTO_DRIVER_SINGLE_EXECUTE: suggestion-only in EVERY mode now.
        // The eager `if (autoExecute) return doTheNextThing()` branch that
        // used to live here was a second execution path: paired with the
        // engaged SpeedController concur in updateEvent it ran the action
        // twice on the same patch. Execution flows solely through concur —
        // every route in Actions/ gates its click emission on
        // aiPackage.execute, so this suggest run is side-effect-free.
        this.lastAIPackage = this.currentAIPackage;
        this._buildNewAIPackage();
        aiRouter(this.gamePlayerController, this.currentAIPackage);
        this.modified = performance.now();
        this.currentAIPackage.done();
    };

    doTheNextThing = () => {
        this._buildNewAIPackage(true);
        aiRouter(this.gamePlayerController, this.currentAIPackage);
        this.modified = performance.now();
        this.currentAIPackage.done();
    }

    belay = () => {
        window.dispatchEvent(new Event('show-manual-controls'));
    };

    concur = () => {
        this.doTheNextThing();
    };

    autoEngaged = () => {
        window.dispatchEvent(new Event('hide-manual-controls'));
    }

}
